import React, { FC, useState } from 'react';
import styled from 'styled-components';

import ChevronSVG from '../../assets/icons/chevron-open.svg';
import { colors, MOBILE_BREAKPOINT, TABLET_BREAKPOINT } from '../constants';

import Heading from './Heading';
import { ModalWrapper, ModalProps } from './ModalWrapper';
import SubHeading from './SubHeading';

interface TipItemStyleProps {
  $isOpen: boolean;
}

// Styles ------------------------------------------------------------
const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 37rem;
  height: 20rem;
  background: #ffffff;
  box-sizing: border-box;
  @media (max-width: ${TABLET_BREAKPOINT}) {
    width: 100%;
    height: 18.4375rem;
  }
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 100%;
    height: 28.5rem;
  }
`;
ContentContainer.displayName = 'ContentContainer';

const ModalHeading = styled(Heading)`
  margin: 0 2.75rem 1.5rem;
  font-size: 2rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    margin: 0 1rem 1.25rem;
    font-size: 1.5rem;
  }
`;
ModalHeading.displayName = 'ModalHeading';

const TipItemContainer = styled.div<TipItemStyleProps>`
  width: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 1.125rem 0.0625rem;
  cursor: pointer;
  ${(props: TipItemStyleProps) =>
    !props.$isOpen &&
    `
    &:hover {
      background-color: #FAFAFA; // show hover only when questions are closed
    }
  `}
`;
TipItemContainer.displayName = 'TipItemContainer';

const TipItemHeadingRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
`;
TipItemHeadingRow.displayName = 'TipItemHeadingRow';

const TipItemHeading = styled.h2`
  color: ${colors.TEXT_SECONDARY};
  font-size: 1.125rem;
  flex: 1;
  margin-right: 0.5rem;
  line-height: 1.5rem;
  @media (max-width: ${MOBILE_BREAKPOINT}) {
    font-size: 1rem;
  }
`;
TipItemHeading.displayName = 'TipItemHeading';

const TipItemContent = styled(SubHeading)`
  text-align: left;
  margin: 0.625rem 1.3125rem 0 0;
  font-size: 0.875rem;
  line-height: 1.5rem;
`;
TipItemContent.displayName = 'TipItemContent';

const ChevronIcon = styled(ChevronSVG)<TipItemStyleProps>`
  width: 1.125rem;
  height: auto;
  margin: 0.5rem 0 0;
  ${(props: TipItemStyleProps) =>
    props.$isOpen &&
    `
    transform: rotate(180deg);
  `}
`;
ChevronIcon.displayName = 'ChevronIcon';

const HR = styled.hr`
  width: 100%;
  height: auto;
  border: none;
  border-top: 0.0625rem solid #f2f2f2;
  margin: 0;
`;
HR.displayName = 'HR';

const tipsElements = [
  {
    heading: 'Do I connect my checking or savings account?',
    content: [
      'You should connect to a <i>checking</i> account that receives your income and/or regular deposits. Just remember, the account you select should have a cash-positive balance. You will also be able to use this bank account to set up automatic payments.',
    ],
    isOpen: true,
  },
  {
    heading: 'I can’t find my bank, what can I do? ',
    content: [
      'Unfortunately, connecting your bank through Plaid is the only way we can verify your information to move forward with your application. If you cannot find your primary bank in the Plaid search bar, you can try searching for another bank that you use regularly.',
    ],
    isOpen: false,
  },
  {
    heading: 'I found my bank but can’t log in, what can I do?',
    content: [
      'If you’re having issues with your banking username and/or password, you can actually reset your password from the Plaid window. The other option is to troubleshoot your login details from your bank’s website.',
      'Once you have your correct login information, you can use it to connect your bank to Prosper with a little help from Plaid!',
      'If you’re still having issues connecting, and you’re confident your username and password are correct, check to see if your bank requires your permission to connect a third-party app. In this case, you may be able to alter your settings at your bank to grant Plaid permission to connect.',
    ],
    isOpen: false,
  },
];

export const BankConnectTipsModal: FC<ModalProps> = ({
  showModal,
  setShowModal,
}) => {
  const [tipsItems, setTipsItems] = useState(tipsElements);

  // Opens/closes tip item drawer on click
  function onTipItemClick(clickedIndex: number) {
    const updatedTips = tipsItems.map((item, index) => ({
      ...item,
      isOpen: index === clickedIndex ? !item.isOpen : false,
    }));
    setTipsItems(updatedTips);
  }

  return (
    <ModalWrapper
      id="bank-connect-tips-modal"
      showModal={showModal}
      setShowModal={setShowModal}
      marginBottom="0"
    >
      <ContentContainer>
        <ModalHeading>Tips on connecting to your bank</ModalHeading>
        {tipsItems.map((tipItem, index) => (
          <React.Fragment key={tipItem.heading}>
            <TipItemContainer
              $isOpen={tipItem.isOpen}
              onClick={() => onTipItemClick(index)}
            >
              <TipItemHeadingRow>
                <TipItemHeading>{tipItem.heading}</TipItemHeading>
                <ChevronIcon $isOpen={tipItem.isOpen} />
              </TipItemHeadingRow>
              {tipItem.isOpen &&
                tipItem.content.map((paragraph) => (
                  <TipItemContent
                    key={paragraph}
                    dangerouslySetInnerHTML={{ __html: paragraph }}
                  />
                ))}
            </TipItemContainer>
            {index !== tipsItems.length - 1 && <HR />}
          </React.Fragment>
        ))}
      </ContentContainer>
    </ModalWrapper>
  );
};
